import React, { useState, useEffect } from "react";
import { Badge, Modal, Button, Typography, Space, Divider, Spin } from "antd";
import { UpOutlined, InfoCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import VersionService from "@/services/version";
import type { AppVersion, UpdateInfo } from "@/services/version";
import { useAppDispatch } from "@redux/store";
import { saveSysMessage } from "@redux/slice/api";

const { Text, Title } = Typography;

interface VersionControlProps {
  /** Additional CSS class name */
  className?: string;
}

const VersionControl: React.FC<VersionControlProps> = ({ className }) => {
  const [currentVersion, setCurrentVersion] = useState<AppVersion | null>(null);
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const versionService = VersionService.getInstance();

    // Get current version
    const version = versionService.getCurrentVersion();
    setCurrentVersion(version);

    // Listen for update available events
    const handleUpdateAvailable = () => {
      setUpdateInfo({
        hasUpdate: true,
        currentVersion: version.version,
        updateAvailable: true,
      });
    };

    window.addEventListener("app-update-available", handleUpdateAvailable as EventListener);

    return () => {
      window.removeEventListener("app-update-available", handleUpdateAvailable as EventListener);
    };
  }, []);

  const handleVersionClick = () => {
    setIsModalVisible(true);
  };

  const handleCheckForUpdates = async () => {
    if (isChecking) return;

    setIsChecking(true);
    try {
      const versionService = VersionService.getInstance();
      const info = await versionService.checkForUpdates();
      setUpdateInfo(info);

      // Show message based on update availability
      if (info.updateAvailable) {
        dispatch(
          saveSysMessage({
            type: "info",
            content: "Update available! You can update now.",
          }),
        );
      } else {
        dispatch(
          saveSysMessage({
            type: "success",
            content: "You're running the latest version of the app.",
          }),
        );
      }
    } catch (error) {
      console.error("Failed to check for updates:", error);
      dispatch(
        saveSysMessage({
          type: "error",
          content: "Failed to check for updates. Please try again.",
        }),
      );
    } finally {
      setIsChecking(false);
    }
  };

  const handleUpdateNow = async () => {
    setIsUpdating(true);
    try {
      const versionService = VersionService.getInstance();
      await versionService.applyUpdate();
    } catch (error) {
      console.error("Failed to apply update:", error);
      setIsUpdating(false);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const hasUpdate = updateInfo?.updateAvailable ?? false;
  const versionDisplay = currentVersion?.version ?? "1.0.0";

  return (
    <>
      <div className={className} onClick={handleVersionClick}>
        <span>v{versionDisplay}</span>
      </div>

      <Modal
        title={
          <Space align="center">
            <UpOutlined />
            <span>App Version</span>
          </Space>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={500}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {/* Current Version Info */}
          <div>
            <Title level={4}>Current Version</Title>
            <Space direction="vertical" size="small">
              <Text strong>Version: {currentVersion?.version}</Text>
              <Text type="secondary">Build: {currentVersion?.buildTime ? new Date(currentVersion.buildTime).toLocaleString() : "Unknown"}</Text>
              {currentVersion?.gitCommit && <Text type="secondary">Commit: {currentVersion.gitCommit.substring(0, 8)}</Text>}
            </Space>
          </div>

          <Divider />

          {/* Update Status */}
          {hasUpdate ? (
            <div>
              <Title level={4} style={{ color: "#ff4d4f" }}>
                <InfoCircleOutlined /> Update Available
              </Title>
              <Text>A new version of the app is available. Update now to get the latest features and improvements.</Text>

              <Space style={{ marginTop: 16 }}>
                <Button type="primary" icon={<UpOutlined />} loading={isUpdating} onClick={handleUpdateNow}>
                  {isUpdating ? "Updating..." : "Update Now"}
                </Button>
                <Button onClick={handleModalClose}>Later</Button>
              </Space>
            </div>
          ) : (
            <div>
              <Title level={4} style={{ color: "#52c41a" }}>
                <CheckCircleOutlined /> Up to Date
              </Title>
              <Text>You&apos;re running the latest version of the app.</Text>

              <div style={{ marginTop: 16 }}>
                <Button icon={isChecking ? <Spin size="small" /> : <UpOutlined />} loading={isChecking} onClick={handleCheckForUpdates}>
                  Check for Updates
                </Button>
              </div>
            </div>
          )}

          {/* Development Mode Info */}
          {process.env.NODE_ENV === "development" && (
            <>
              <Divider />
              <div>
                <Text type="warning" strong>
                  Development Mode
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Version control features are available for testing. In production, updates will be handled automatically by the service worker.
                </Text>
              </div>
            </>
          )}
        </Space>
      </Modal>
    </>
  );
};

export default VersionControl;
